import { Address } from "@prisma/client";

export function getAddressLabel(
  address:
    | {
        address?: string | null;
        zipCode?: string | null;
        city?: string | null;
        name?: string | null;
      }
    | null
    | undefined
): string | null {
  if (!address) return "";

  let addressString = "";

  if (address.name) {
    addressString += address.name + " ";
  }

  if (address.address) {
    addressString += address.address + " ";
  } else {
    if (address.zipCode) {
      addressString += address.zipCode + " ";
    }

    if (address.city) {
      addressString += address.city;
    }
  }

  return addressString;
}

export function getGmapsLink(
  address: Pick<Address, "address" | "lat" | "long" | "zipCode" | "city">
) {
  if (address.address) {
    return `https://maps.google.com/?q=${encodeURI(address.address)}`;
  }

  if (address.lat && address.long) {
    return `https://maps.google.com/?q=${encodeURI(
      address.lat + "," + address.long
    )}`;
  }

  if (address.zipCode && address.city) {
    return `https://maps.google.com/?q=${encodeURI(
      address.address + "," + address.zipCode + " " + address.city
    )}`;
  }

  return `${address.address || ""}, ${address.zipCode || ""} ${
    address.city || ""
  }`;
}

/**
 * Formats a French SIRET or SIREN number by adding spaces for readability.
 *
 * In France:
 * - A SIREN is a 9-digit business identification number.
 * - A SIRET is a 14-digit establishment identification number, which includes the SIREN followed by a 5-digit NIC.
 *
 * This function formats:
 * - A 9-digit SIREN as 'XXX XXX XXX'.
 * - A 14-digit SIRET as 'XXX XXX XXX XXXXX'.
 *
 * If the input is neither 9 nor 14 digits long, it returns the original string.
 *
 * @param {string} siret - The SIRET or SIREN number as a string of digits.
 * @returns {string} The formatted SIRET or SIREN with spaces, or the original string if it doesn't match expected lengths.
 *
 * @example
 * // Returns '123 456 789'
 * formatSiret('123456789');
 *
 * @example
 * // Returns '123 456 789 00012'
 * formatSiret('12345678900012');
 */
export function formatSiret(siret: string) {
  if (siret.length === 9)
    return siret.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3");
  return siret.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, "$1 $2 $3 $4");
}

if (formatSiret("12345678900012") !== "123 456 789 00012") {
  throw new Error(
    `formatSiret is broken with siret : formatSiret("12345678900012") === "${formatSiret("12345678900012")}"`
  );
}

if (formatSiret("123456789") !== "123 456 789") {
  throw new Error(
    `formatSiret is broken with siren : formatSiret("123456789") === "${formatSiret("123456789")}"`
  );
}

/**
 *
 * @param {string} vat form FR22501471056
 * @returns {string} The formatted VAT number with spaces, like FR 22.501.471.056
 */
export function formatVAT(vat: string) {
  return vat.replace(/(\w{2})(\d{2})(\d{3})(\d{3})(\d{3})/, "$1 $2.$3.$4.$5");
}
